import React, { useCallback, useEffect, useRef, useState } from "react";
import AdminService from "src/services/admin.services";
import { Order } from "src/services/types";
import Modal from "./Modal";
import html2canvas from "html2canvas";

import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import DeliveryLocation from "../deliverylocation/DeliveryLocation";
import { Link, useSearchParams } from "react-router-dom";
import PrintOrderButton from "./PrintOrderButton";
import { AppLayout } from "src/components/layouts";
import BackButton from "src/components/BackButton";
import AssignRider from "../logistic/components/AssignRider";
import { format } from "date-fns";
import CustomSelect from "../../components/ui/CustomSelect";
import FoodLoader from "src/components/Loader";
import { QueryFunctionContext, useQuery } from "react-query";
import { toast } from "react-toastify";
import { NIGERIAN_STATES } from "../../utils/constants";

//import { LocationData } from 'src/services/types';

// Function to map coordinates to Nigerian states
const getStateFromCoordinates = (
  latitude: number,
  longitude: number
): string => {
  // Nigerian state coordinates (approximate center points)
  const stateCoordinates: {
    [key: string]: { lat: number; lng: number; radius: number };
  } = {
    Abuja: { lat: 9.082, lng: 7.3986, radius: 10.0 },
    Lagos: { lat: 6.5244, lng: 3.3792, radius: 10.0 },
    Kano: { lat: 11.9914, lng: 8.5311, radius: 10.0 },
    Kaduna: { lat: 10.5222, lng: 7.4384, radius: 10.0 },
    Katsina: { lat: 12.9914, lng: 7.6011, radius: 10.0 },
    Jigawa: { lat: 12.57, lng: 9.56, radius: 10.0 },
    Yobe: { lat: 12.0, lng: 11.5, radius: 10.0 },
    Borno: { lat: 11.8333, lng: 13.15, radius: 10.0 },
    Adamawa: { lat: 9.3273, lng: 12.3984, radius: 10.0 },
    Taraba: { lat: 8.8833, lng: 11.3667, radius: 10.0 },
    Gombe: { lat: 10.2897, lng: 11.1673, radius: 10.0 },
    Bauchi: { lat: 10.3158, lng: 9.8444, radius: 10.0 },
    Plateau: { lat: 9.8965, lng: 8.8583, radius: 10.0 },
    Nasarawa: { lat: 8.5, lng: 8.0, radius: 10.0 },
    Niger: { lat: 9.6, lng: 6.55, radius: 10.0 },
    Kebbi: { lat: 12.45, lng: 4.2, radius: 10.0 },
    Sokoto: { lat: 13.0667, lng: 5.2333, radius: 10.0 },
    Zamfara: { lat: 12.1667, lng: 6.1667, radius: 10.0 },
    Kogi: { lat: 7.8, lng: 6.7333, radius: 10.0 },
    Kwara: { lat: 8.5, lng: 4.55, radius: 10.0 },
    Oyo: { lat: 7.85, lng: 3.9333, radius: 10.0 },
    Osun: { lat: 7.75, lng: 4.5667, radius: 10.0 },
    Ondo: { lat: 7.25, lng: 5.2, radius: 10.0 },
    Ekiti: { lat: 7.6333, lng: 5.2167, radius: 10.0 },
    Ogun: { lat: 7.1667, lng: 3.35, radius: 10.0 },
    Edo: { lat: 6.3176, lng: 5.6145, radius: 10.0 },
    Delta: { lat: 5.5, lng: 5.75, radius: 10.0 },
    Rivers: { lat: 4.75, lng: 7.0, radius: 10.0 },
    Bayelsa: { lat: 4.75, lng: 6.25, radius: 10.0 },
    "Akwa Ibom": { lat: 5.0, lng: 7.85, radius: 10.0 },
    "Cross River": { lat: 5.5, lng: 8.0, radius: 10.0 },
    Ebonyi: { lat: 6.25, lng: 8.1, radius: 10.0 },
    Enugu: { lat: 6.45, lng: 7.5, radius: 10.0 },
    Anambra: { lat: 6.2, lng: 7.0, radius: 10.0 },
    Imo: { lat: 5.5, lng: 7.0, radius: 10.0 },
    Abia: { lat: 5.5, lng: 7.5, radius: 10.0 },
  };

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Find the closest state
  let closestState = "all";
  let minDistance = Infinity;

  for (const [state, coords] of Object.entries(stateCoordinates)) {
    const distance = calculateDistance(
      latitude,
      longitude,
      coords.lat,
      coords.lng
    );

    if (distance < minDistance && distance <= coords.radius) {
      minDistance = distance;
      closestState = state;
    }
  }

  return closestState;
};
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const adminService = new AdminService();

// Declare locally or in a shared file
type OrderStatus =
  | "delivered"
  | "pending"
  | "cancelled"
  | "confirmed"
  | "declined"
  | "all"
  | "location";

type OrderFilter = "all" | "location" | OrderStatus;

// Define LocationData type
type LocationData = {
  latitude: number;
  longitude: number;
};

const DATE_PLACEHOLDER = "0001-01-01T00:00:00Z";

const formatCurrency = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return "₦0";
  }
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(value);
};

const formatDateTime = (value?: string | null) => {
  if (!value || value === DATE_PLACEHOLDER) {
    return "—";
  }
  try {
    return format(new Date(value), "PPpp");
  } catch (error) {
    return "—";
  }
};

const fetchOrders = async (context: QueryFunctionContext) => {
  const [, pageParam, perPageParam, searchTerm, filter, longitude, latitude] =
    context.queryKey;

  const currentPage = Number(pageParam ?? 1);
  const ordersPerPage = Number(perPageParam ?? 100);

  const fetchedOrders = await adminService.getOrders(
    currentPage,
    ordersPerPage,
    (searchTerm as string) ?? "",
    filter as any,
    longitude as number | undefined,
    latitude as number | undefined
  );

  if (!Array.isArray(fetchedOrders)) {
    throw new Error("Unexpected response format");
  }

  return fetchedOrders;
};

const NotificationWrapper: React.FC = () => {
  // State declarations
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredData, setFilteredData] = useState<Order[]>([]);

  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const ordersPerPage = 100; // Display 40 orders per page
  // const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  // const [successMessage] = useState<string | null>(null);
  // const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [filter] = useState<string>("day");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [filterStatus] = useState<OrderStatus>("all");
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [activeTab, setActiveTab] = useState<"orders" | "assignRider">(
    "orders"
  );
  const [currentFilter, setCurrentFilter] = useState<OrderFilter>("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [searching, setSearching] = useState<boolean>(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [, setIsLoadingOrder] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [, setModalType] = useState<"success" | "error">("success");
  const [selectedState, setSelectedState] = useState<string>("all");
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const orderId = searchParams.get("orderId");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside any dropdown menu
      if (
        !target.closest(".dropdown-menu-container") &&
        !target.closest("button[aria-label='Actions']")
      ) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  // Filter orders by coordinates
  // const filterByCoordinates = (
  //   orders: Order[],
  //   longitude?: number,
  //   latitude?: number,
  //   maxDistance: number = 1000000
  // ): Order[] => {
  //   if (!longitude || !latitude) return orders;

  //   return orders.filter((order) => {
  //     const { longitude: orderLongitude, latitude: orderLatitude } = order;
  //     if (orderLongitude && orderLatitude) {
  //       const distance = getDistanceFromLatLonInMeters(
  //         latitude,
  //         longitude,
  //         orderLatitude,
  //         orderLongitude
  //       );
  //       return distance <= maxDistance;
  //     }
  //     return false;
  //   });
  // };

  // Calculate distance between two coordinates
  // const getDistanceFromLatLonInMeters = (
  //   lat1: number,
  //   lon1: number,
  //   lat2: number,
  //   lon2: number
  // ): number => {
  //   const R = 6371e3;
  //   const φ1 = (lat1 * Math.PI) / 180;
  //   const φ2 = (lat2 * Math.PI) / 180;
  //   const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  //   const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  //   const a =
  //     Math.sin(Δφ / 2) ** 2 +
  //     Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  //   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  //   return R * c;
  // };
  // Inside your component
  const {
    refetch: refetchSelectedOrder,
    isLoading,
    isError,
    error,
  } = useQuery(
    [
      "orders",
      pageNumber,
      ordersPerPage,
      searchTerm.trim() !== "" ? searchTerm : "",
      currentFilter === "location" || currentFilter === "all"
        ? undefined
        : currentFilter,
      currentFilter === "location" ? locationData?.longitude : undefined,
      currentFilter === "location" ? locationData?.latitude : undefined,
    ],
    fetchOrders,
    {
      refetchInterval: 30000,
      keepPreviousData: true,
      enabled: !!pageNumber,
      refetchOnWindowFocus: false,

      onSuccess: (data) => {
        setOrders(data);
        if (!searching) setFilteredData(data);
      },
      onError: (error) => {
        // Error fetching logistics data - silently fail
      },
    }
  );

  const updateOrderStatus = async (orderId: string, status: string) => {
    try {
      // List of valid statuses from API error message
      const validStatuses = [
        "delivered",
        "confirmed",
        "declined",
        "ready",
        "arrived",
        "cancelled",
        "picked up",
      ];

      if (!validStatuses.includes(status)) {
        setModalMessage(
          `Invalid status: ${status}. Allowed values: ${validStatuses.join(
            ", "
          )}`
        );
        setIsModalOpen(true);
        return;
      }

      // Send the update request
      await adminService.updateOrders(orderId, {
        order_status: status,
      });

      // Update local state only after successful API call
      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o._id === orderId ? { ...o, order_status: status } : o
        )
      );

      // Update filtered data as well
      setFilteredData((prevData) =>
        prevData.map((o) =>
          o._id === orderId ? { ...o, order_status: status } : o
        )
      );

      toast.success(`Order updated to ${status} successfully.`);

      // Refetch orders to get the latest data
      refetchSelectedOrder();
    } catch (error: any) {
      console.error("Error updating order:", error);
      const errorMessage =
        error?.message || "Failed to update order. Please try again.";
      toast.error(errorMessage);
      throw error; // Re-throw to let calling function handle it
    }
  };

  const handleAcceptOrder = async (order: Order) => {
    setIsUpdating(true);
    try {
      await updateOrderStatus(order._id, "confirmed");
    } catch (error) {
      // Error is already handled in updateOrderStatus
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeclineOrder = async (order: Order) => {
    setIsUpdating(true);
    try {
      await updateOrderStatus(order._id, "declined");
    } catch (error) {
      // Error is already handled in updateOrderStatus
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelOrder = async (order: Order) => {
    if (order.order_status === "delivered") {
      toast.warning(
        "This order has already been delivered and cannot be canceled."
      );
      return;
    }

    if (order.order_status === "cancelled") {
      toast.info("This order has already been cancelled.");
      return;
    }

    if (order.order_status === "declined") {
      toast.info("This order has already been declined.");
      return;
    }

    setIsUpdating(true);
    try {
      await updateOrderStatus(order._id, "cancelled");
      // Note: updateOrderStatus already shows success toast and updates state
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      toast.error("Failed to cancel the order. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };
  // useEffect(() => {
  //   if (typeof fetchOrders === "function") {
  //     fetchOrders();
  //   }
  // }, [filterStatus]);

  // Apply search filter - search by restaurant name and filter by state
  const applySearchFilter = useCallback(
    (orders: Order[]) => {
      let filtered = [...orders];

      // Apply state filter
      if (selectedState !== "all") {
        filtered = filtered.filter((order) => {
          // Use coordinates from order.items[0].meal?.location?.Coordinates
          if (
            order.items &&
            order.items.length > 0 &&
            order.items[0].meal?.location?.Coordinates
          ) {
            try {
              const coordinates = order.items[0].meal.location.Coordinates;

              // Coordinates are in [longitude, latitude] format
              const [longitude, latitude] = coordinates;

              // Get state from coordinates
              const stateFromCoordinates = getStateFromCoordinates(
                latitude,
                longitude
              );

              if (stateFromCoordinates !== "all") {
                if (stateFromCoordinates === selectedState) {
                  return true;
                }
              }
            } catch (error) {
              // Error processing coordinates - silently fail
            }
          }

          return false;
        });
      }

      // Apply search filter - search by restaurant name, order code, and order ID
      if (searchTerm.trim() !== "") {
        const lowerCaseSearchTerm = searchTerm.trim().toLowerCase();
        filtered = filtered.filter(
          (order) =>
            (order.restaurant_name &&
              order.restaurant_name
                .toLowerCase()
                .includes(lowerCaseSearchTerm)) ||
            (order.order_code &&
              order.order_code.toLowerCase().includes(lowerCaseSearchTerm)) ||
            (order._id && order._id.toLowerCase().includes(lowerCaseSearchTerm))
        );
      }

      return filtered;
    },
    [searchTerm, selectedState]
  );

  // Paginate filtered orders
  const startIndex = (pageNumber - 1) * ordersPerPage;
  const paginatedOrders = filteredData
    .slice(startIndex, startIndex + ordersPerPage)
    .map((order, index) => ({
      ...order,
      serialNumber: startIndex + index + 1,
      uniqueKey: `${order._id}-${startIndex + index}`,
    }));

  useEffect(() => {
    if (filterStatus === "location" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocationData({ latitude, longitude });
        },
        (error) => {
          // Error fetching geolocation - silently fail
        },
        { enableHighAccuracy: true }
      );
    }
  }, [filterStatus]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setPageNumber(page);
    }
  };

  // Fetch new orders when pageNumber changes
  // useEffect(() => {
  //   fetchOrders();

  //   let interval: NodeJS.Timeout | undefined;

  //   if (!searching) {
  //     interval = setInterval(fetchOrders, 3000000); // 60 seconds
  //   }

  //   return () => {
  //     if (interval) clearInterval(interval);
  //   };
  // }, [pageNumber, currentFilter, locationData, searching]);

  useEffect(() => {
    // Disable scroll when modal opens
    document.body.classList.add("no-scroll");
    return () => {
      // Restore scroll when modal closes
      document.body.classList.remove("no-scroll");
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const filtered = applySearchFilter(orders);
      setFilteredData(filtered);
      setSearching(searchTerm.trim() !== "");
      const pages = Math.max(1, Math.ceil(filtered.length / ordersPerPage));
      setTotalPages(pages);
      if (pageNumber > pages) {
        setPageNumber(pages);
      }
    }, 300); // Debounce delay

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [
    searchTerm,
    orders,
    selectedState,
    ordersPerPage,
    pageNumber,
    applySearchFilter,
  ]);

  // Search input handler
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
  };

  // useEffect(() => {
  //   fetchOrders();
  // }, [filterStatus]);

  const openModal = (order: Order) => {
    setSelectedOrder(order);
    setSearchParams((prev: any) => {
      prev.set("orderId", order._id);
      return prev;
    });
  };

  const closeModal = () => {
    setSelectedOrder(null);
    searchParams.delete("orderId");
    setSearchParams(searchParams);
  };

  // Helper to resolve valid entity id (string, object, or array) safely
  const getEntityId = (entity: any): string | undefined => {
    if (!entity) return undefined;
    if (typeof entity === "string") {
      const id = entity.trim();
      const isValid =
        /^[a-fA-F0-9]{24}$/.test(id) && id !== "000000000000000000000000";
      return isValid ? id : undefined;
    }
    if (Array.isArray(entity)) {
      const id = entity[0]?._id;
      const isValid =
        typeof id === "string" &&
        /^[a-fA-F0-9]{24}$/.test(id) &&
        id !== "000000000000000000000000";
      return isValid ? id : undefined;
    }
    const id = entity?._id;
    const isValid =
      typeof id === "string" &&
      /^[a-fA-F0-9]{24}$/.test(id) &&
      id !== "000000000000000000000000";
    return isValid ? id : undefined;
  };

  useEffect(() => {
    if (orderId && !selectedOrder) {
      setIsLoadingOrder(true);
      adminService
        .getOrderDetails(orderId)
        .then((order) => setSelectedOrder(order))
        .catch(() => {
          // Optionally handle not-found state
        })
        .finally(() => setIsLoadingOrder(false));
    }
  }, [orderId, selectedOrder]);

  // Function to prepare data for download (as CSV)
  const prepareDownloadData = (orders: Order[]) => {
    const csvRows = [
      ["Order Code", "Restaurant Name", "Date Delivered", "Order Status"], // Header row
      ...orders.map((order) => [
        order._id,
        order.order_code,
        order.restaurant_name,
        order.subtotal,
        order.total,
        order.delivery_fee,
        order.tax,

        order.items
          .map(
            (item: { meal: { name: any }; qty: any }) =>
              `${item.meal.name} (${item.qty})`
          )
          .join(", "),
        order.user,
        order.order_status,
        order.payment_method,
        new Date(order.date_delivered).toLocaleDateString(),
        new Date(order.pickup_time).toLocaleDateString(),
        new Date(order.enroute_time).toLocaleDateString(),
        new Date(order.created_at).toLocaleDateString(),

        order.agent,
        order.agent_delivered_order_upload,
        order.delivery_location,

        order.discount.order,
        order.discount.delivery,
        order.voucher,

        order.note,
        order.user_received_order_upload,
      ]),
    ];

    const csvContent = csvRows.map((row) => row.join(",")).join("\n");

    // Create a download link for CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `orders_${filter}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadInvoice = () => {
    const captureElement = document.getElementById("order-details");

    if (captureElement && selectedOrder) {
      html2canvas(captureElement, {
        useCORS: true,
        scale: 2,
        logging: true,
      })
        .then((canvas) => {
          const link = document.createElement("a");
          link.href = canvas.toDataURL("image/png");
          link.download = `Order_${selectedOrder._id}.png`;
          link.click();
        })
        .catch((error) => {
          // Error capturing the element - silently fail
        });
    }
  };

  if (isLoading)
    return (
      <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
        <FoodLoader
          size="md"
          text="Fetching Orders"
          theme="amber"
          className="scale-75"
        />
      </div>
    );
  if (isError) return <div>Error: {(error as Error).message}</div>;

  const handleDeliverOrder = async (id: string) => {
    try {
      const response = await adminService.updateOrderAsDelivered({
        order_id: id,
      });
      if (response?.message === "order delivered successfully") {
        setModalMessage("Order delivered successfully");
        setModalType("success");
        setIsModalOpen(true);
        refetchSelectedOrder();
      } else {
        setModalMessage(response.error);
        setModalType("error");
        setIsModalOpen(true);
        refetchSelectedOrder();
      }
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        "Order can not be delivered at the moment";
      setModalMessage(errorMessage);
      setModalType("error");
      setIsModalOpen(true);
      refetchSelectedOrder();
    }
  };

  const handleCancelDeliveryRequest = async (id: string) => {
    try {
      const response = await adminService.cancelDeliveryRequest({
        parcel_id: "",
        order_id: id,
      });
      if (response?.message === "delivery request cancelled successfully") {
        setModalMessage("Delivery request cancelled successfully");
        setModalType("success");
        setIsModalOpen(true);
        refetchSelectedOrder();
      } else {
        setModalMessage(response.error);
        setModalType("error");
        setIsModalOpen(true);
        refetchSelectedOrder();
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "Error cancelling delivery request";
      setModalMessage(errorMessage);
      setModalType("error");
      setIsModalOpen(true);
      refetchSelectedOrder();
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          closeModal(); // Close the details modal after response modal is closed
        }}
        title="Order Status"
      >
        <p>{modalMessage}</p>
      </Modal>
      <AppLayout>
        <div className="container mx-auto px-2 py-4">
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-2 overflow-x-hidden">
              {/* Top Section */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
                <div className="flex items-center gap-4">
                  <BackButton />
                  <h2 className="text-2xl font-bold text-amber-900">
                    Order Management
                  </h2>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2">
                  <button
                    className={`py-2 px-4 rounded-md text-sm font-medium ${
                      activeTab === "orders"
                        ? "bg-amber-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => setActiveTab("orders")}
                  >
                    Orders
                  </button>
                  <button
                    className={`py-2 px-4 rounded-md text-sm font-medium ${
                      activeTab === "assignRider"
                        ? "bg-amber-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => setActiveTab("assignRider")}
                  >
                    Assign Rider
                  </button>
                </div>
              </div>

              {/* Content */}
              {isLoading ? (
                <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
                  <FoodLoader
                    size="md"
                    text="Fetching Orders"
                    theme="amber"
                    className="scale-75"
                  />
                </div>
              ) : isError ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-500"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">
                        {(error as Error).message}
                      </p>
                    </div>
                  </div>
                </div>
              ) : activeTab === "orders" ? (
                <div className="w-full leading-normal bg-amber-50 bg-opacity-25 rounded-tl-xl rounded-tr-xl border rounded-xl overflow-x-hidden">
                  <div className="flex flex-wrap justify-between items-center gap-3 p-5 bg-white rounded-lg shadow-sm min-w-0">
                    {/* Search + Download */}
                    <div className="flex flex-wrap items-center gap-3">
                      <input
                        type="text"
                        placeholder="Search by restaurant name, order code, or order ID..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="border border-gray-300 focus:border-blue-400 focus:ring-blue-400 rounded-lg px-4 py-2 text-sm w-72 shadow-sm"
                      />
                      <button
                        onClick={() => prepareDownloadData(filteredData)}
                        className="bg-yellow-400 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Download
                      </button>
                    </div>

                    {/* Filter */}
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      <span className="text-gray-600 text-sm font-medium whitespace-nowrap">
                        Filter by:
                      </span>
                      <div className="w-24 min-w-0">
                        <CustomSelect
                          value={currentFilter}
                          onChange={(value) =>
                            setCurrentFilter(value as OrderStatus)
                          }
                          options={[
                            { label: "All", value: "all" },
                            { label: "Delivered", value: "delivered" },
                            { label: "Pending", value: "pending" },
                            { label: "Confirmed", value: "confirmed" },
                            { label: "Declined", value: "declined" },
                            { label: "Cancelled", value: "cancelled" },
                            { label: "Location", value: "location" },
                          ]}
                          placeholder="Status"
                        />
                      </div>
                      <span className="text-gray-600 text-sm font-medium whitespace-nowrap">
                        State:
                      </span>
                      <div className="w-32 min-w-0">
                        <CustomSelect
                          value={selectedState}
                          onChange={(value) =>
                            setSelectedState(value as string)
                          }
                          options={NIGERIAN_STATES.map((state) => ({
                            label: state === "all" ? "All States" : state,
                            value: state,
                          }))}
                          placeholder="Select State"
                        />
                      </div>
                    </div>

                    {/* Total Orders */}
                    <div className="flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm">
                      <span>Total Orders:</span>
                      <span>{filteredData.length}</span>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left">
                      <thead className="bg-amber-100/70 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 border-b border-amber-200 w-[50px]">
                            S/N
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Order Code
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Restaurant
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Created
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Status
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Payment Method
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Payment Status
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Agent
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Subtotal
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Delivery Fee
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Order Discount
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Delivery Discount
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Voucher
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Delivery Bonus
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Commission
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Total
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Cancelled At
                          </th>
                          <th className="px-4 py-3 border-b border-amber-200 whitespace-nowrap">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedOrders.map((order: Order, index: number) => {
                          const orderAny = order as Record<string, any>;
                          const agentId = orderAny.agent;
                          return (
                            <tr
                              key={order._id}
                              className="hover:bg-amber-50/80"
                            >
                              <td className="px-4 py-3 border-b border-amber-100">
                                {(pageNumber - 1) * ordersPerPage + index + 1}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100">
                                {order.order_code}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100">
                                {order.restaurant_name}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatDateTime(order.created_at)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 capitalize">
                                {order.order_status || "—"}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 capitalize">
                                {orderAny.payment_method || "—"}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 capitalize">
                                {orderAny.payment_status || "—"}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100">
                                {agentId &&
                                agentId !== "000000000000000000000000"
                                  ? agentId
                                  : "Unassigned"}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatCurrency(order.subtotal)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatCurrency(order.delivery_fee)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatCurrency(order.discount?.order)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatCurrency(order.discount?.delivery)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatCurrency(order.voucher)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatCurrency(orderAny.delivery_bonus)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatCurrency(orderAny.commission)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatCurrency(order.total)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100 whitespace-nowrap">
                                {formatDateTime(orderAny.date_cancelled)}
                              </td>
                              <td className="px-4 py-3 border-b border-amber-100">
                                <div className="relative dropdown-menu-container">
                                  <button
                                    onClick={() =>
                                      setOpenDropdownId(
                                        openDropdownId === order._id
                                          ? null
                                          : order._id
                                      )
                                    }
                                    aria-label="Actions"
                                    className="p-2 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-md transition duration-200"
                                    disabled={isUpdating}
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                    </svg>
                                  </button>

                                  {openDropdownId === order._id && (
                                    <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                      <div className="py-1">
                                        {order.order_status === "pending" && (
                                          <>
                                            <button
                                              onClick={() => {
                                                handleAcceptOrder(order);
                                                setOpenDropdownId(null);
                                              }}
                                              className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                              disabled={isUpdating}
                                            >
                                              Accept
                                            </button>
                                            <button
                                              onClick={() => {
                                                handleDeclineOrder(order);
                                                setOpenDropdownId(null);
                                              }}
                                              className="w-full text-left px-4 py-2 text-sm text-orange-700 hover:bg-orange-50 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                              disabled={isUpdating}
                                            >
                                              Decline
                                            </button>
                                            <button
                                              onClick={() => {
                                                handleCancelOrder(order);
                                                setOpenDropdownId(null);
                                              }}
                                              className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                              disabled={isUpdating}
                                            >
                                              Cancel
                                            </button>
                                            <div className="border-t border-gray-100 my-1"></div>
                                          </>
                                        )}
                                        <button
                                          onClick={() => {
                                            openModal(order);
                                            setOpenDropdownId(null);
                                          }}
                                          className="w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50 transition duration-150"
                                        >
                                          View Details
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {activeTab === "orders" && (
                    <div className="flex justify-between items-center p-4">
                      {/* Previous Button */}
                      <button
                        onClick={() => handlePageChange(pageNumber - 1)}
                        disabled={pageNumber === 1}
                        className={`px-4 py-2 border rounded-lg transition duration-200 ${
                          pageNumber === 1
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        Previous
                      </button>

                      {/* Page Number Display */}
                      <span className="px-4 py-2 border rounded-lg bg-white text-gray-700">
                        Page {pageNumber} of {totalPages || 1}
                      </span>

                      {/* Next Button */}
                      <button
                        onClick={() => handlePageChange(pageNumber + 1)}
                        disabled={pageNumber >= totalPages}
                        className={`px-4 py-2 border rounded-lg transition duration-200 ${
                          pageNumber >= totalPages
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  )}

                  {/* Modal for Order Details */}
                  {selectedOrder && (
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
                      <div
                        id="order-details"
                        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Header */}
                        <div className="sticky top-0 bg-white p-4 sm:p-6 border-b border-gray-200 z-10 rounded-t-lg">
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                                Order #{selectedOrder.order_code}
                              </h2>
                              <div className="flex flex-wrap items-center mt-2 gap-2">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    selectedOrder.order_status === "delivered"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-yellow-100 text-yellow-800"
                                  }`}
                                >
                                  {selectedOrder.order_status}
                                </span>
                                <span className="text-xs sm:text-sm text-gray-500">
                                  {new Date(
                                    selectedOrder.created_at
                                  ).toLocaleString()}
                                </span>
                              </div>
                            </div>

                            <button
                              onClick={closeModal}
                              className="text-gray-400 hover:text-gray-600 transition p-1 flex-shrink-0"
                              aria-label="Close"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-2 mt-4">
                            {[
                              "enroute",
                              "picked_up",
                              "declined",
                              "preparing",
                            ].includes(selectedOrder?.order_status || "") && (
                              <button
                                onClick={() =>
                                  handleCancelDeliveryRequest(selectedOrder._id)
                                }
                                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition text-sm font-medium w-full sm:w-auto"
                              >
                                Cancel delivery request
                              </button>
                            )}
                            {selectedOrder?.order_status === "arrived" && (
                              <button
                                onClick={() =>
                                  handleDeliverOrder(selectedOrder._id)
                                }
                                className="bg-yellow-500 text-white font-medium py-2 px-6 rounded-lg transition flex items-center justify-center text-sm w-full sm:w-auto"
                              >
                                Deliver order
                              </button>
                            )}
                            {selectedOrder?.order_status !== "delivered" &&
                              selectedOrder?.order_status !== "cancelled" &&
                              selectedOrder?.order_status !== "declined" && (
                                <button
                                  onClick={() =>
                                    handleCancelOrder(selectedOrder)
                                  }
                                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-6 rounded-lg transition flex items-center justify-center text-sm w-full sm:w-auto"
                                >
                                  <svg
                                    className="w-5 h-5 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                  Cancel Order
                                </button>
                              )}
                          </div>
                        </div>

                        {/* Scrollable Content */}
                        <div className="overflow-y-auto flex-1 p-4 sm:p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                            {/* Left Column - Order Timeline */}
                            <div>
                              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-900">
                                Order Timeline
                              </h3>
                              <div className="space-y-3 sm:space-y-4">
                                <TimelineItem
                                  title="Order Placed"
                                  date={selectedOrder.created_at}
                                  active
                                />
                                <TimelineItem
                                  title="Pickup Time"
                                  date={selectedOrder.pickup_time}
                                  active={!!selectedOrder.pickup_time}
                                />
                                <TimelineItem
                                  title="Enroute"
                                  date={selectedOrder.enroute_time}
                                  active={!!selectedOrder.enroute_time}
                                />
                                <TimelineItem
                                  title="Delivered"
                                  date={selectedOrder.date_delivered}
                                  active={!!selectedOrder.date_delivered}
                                />
                              </div>

                              {/* Delivery Location */}
                              {selectedOrder.delivery_location && (
                                <div className="mt-6 sm:mt-8">
                                  <h3 className="text-base sm:text-lg font-semibold mb-3 text-gray-900">
                                    Delivery Location
                                  </h3>
                                  <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
                                    <DeliveryLocation
                                      locationId={
                                        selectedOrder.delivery_location as string
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Right Column - Order Details */}
                            <div>
                              <div
                                className={`grid grid-cols-1 ${
                                  getEntityId(selectedOrder.user) &&
                                  getEntityId(selectedOrder.agent)
                                    ? "sm:grid-cols-2"
                                    : "sm:grid-cols-1"
                                } gap-3 sm:gap-4 mb-6`}
                              >
                                {getEntityId(selectedOrder.user) && (
                                  <UserCard
                                    id={getEntityId(selectedOrder.user)}
                                    type="user"
                                    title="Customer"
                                    icon="👤"
                                  />
                                )}
                                {getEntityId(selectedOrder.agent) && (
                                  <UserCard
                                    id={getEntityId(selectedOrder.agent)}
                                    type="agent"
                                    title="Delivery Agent"
                                    icon="🚚"
                                  />
                                )}
                              </div>

                              {/* Order Items */}
                              <h3 className="text-base sm:text-lg font-semibold mb-3 text-gray-900">
                                Items ({selectedOrder.items.length})
                              </h3>
                              <div className="space-y-3 sm:space-y-4 mb-6">
                                {selectedOrder.items.map((item) => (
                                  <div
                                    key={item.meal._id}
                                    className="flex gap-3 border-b border-gray-100 pb-3 sm:pb-4"
                                  >
                                    {item.meal.image_url && (
                                      <img
                                        src={
                                          item.meal.image_url ||
                                          "/placeholder.svg"
                                        }
                                        alt={item.meal.name}
                                        className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg flex-shrink-0"
                                      />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex justify-between gap-2">
                                        <h4 className="font-medium text-sm sm:text-base truncate">
                                          {item.meal.name}
                                        </h4>
                                        <p className="font-semibold text-sm sm:text-base flex-shrink-0">
                                          ₦{item.meal.price.amount}
                                        </p>
                                      </div>
                                      <p className="text-xs sm:text-sm text-gray-500 mt-1 line-clamp-2">
                                        {item.meal.description}
                                      </p>
                                      <div className="flex justify-between items-center mt-2">
                                        <span className="text-xs sm:text-sm text-gray-600">
                                          Qty: {item.qty}
                                        </span>
                                        <span className="font-medium text-sm sm:text-base">
                                          ₦
                                          {(
                                            item.meal.price.amount * item.qty
                                          ).toLocaleString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Payment Summary */}
                              <h3 className="text-base sm:text-lg font-semibold mb-3 text-gray-900">
                                Payment Summary
                              </h3>
                              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Method
                                    </span>
                                    <span className="font-medium">
                                      {selectedOrder.payment_method}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Subtotal
                                    </span>
                                    <span>₦{selectedOrder.subtotal}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Delivery Fee
                                    </span>
                                    <span>
                                      ₦
                                      {selectedOrder.delivery_fee.toLocaleString()}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Service Charge
                                    </span>
                                    <span>₦{selectedOrder.tax}</span>
                                  </div>
                                  {selectedOrder.discount.order > 0 && (
                                    <div className="flex justify-between text-green-600 text-sm">
                                      <span>Order Discount</span>
                                      <span>
                                        -₦{selectedOrder.discount.order}
                                      </span>
                                    </div>
                                  )}
                                  {selectedOrder.discount.delivery > 0 && (
                                    <div className="flex justify-between text-green-600 text-sm">
                                      <span>Delivery Discount</span>
                                      <span>
                                        -₦{selectedOrder.discount.delivery}
                                      </span>
                                    </div>
                                  )}
                                  <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-bold text-sm sm:text-base">
                                    <span>Total</span>
                                    <span>
                                      ₦{selectedOrder.total.toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Attachments */}
                          {(selectedOrder.agent_delivered_order_upload ||
                            selectedOrder.user_received_order_upload) && (
                            <div className="mt-6 sm:mt-8">
                              <h3 className="text-base sm:text-lg font-semibold mb-3 text-gray-900">
                                Attachments
                              </h3>
                              <div className="flex flex-wrap gap-3 sm:gap-4">
                                {selectedOrder.agent_delivered_order_upload && (
                                  <AttachmentPreview
                                    url={
                                      selectedOrder.agent_delivered_order_upload
                                    }
                                    title="Agent Delivery Proof"
                                  />
                                )}
                                {selectedOrder.user_received_order_upload && (
                                  <AttachmentPreview
                                    url={
                                      selectedOrder.user_received_order_upload
                                    }
                                    title="Customer Receipt Proof"
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex flex-col sm:flex-row gap-3 rounded-b-lg">
                          <button
                            onClick={downloadInvoice}
                            className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition w-full sm:flex-1 text-sm font-medium"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Download Invoice
                          </button>

                          <div className="flex gap-3 w-full sm:w-auto">
                            <div className="bg-gray-100 hover:bg-gray-200 rounded-lg transition flex-1 sm:flex-initial flex items-center justify-center">
                              <PrintOrderButton />
                            </div>
                            <button
                              onClick={closeModal}
                              className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition flex-1 sm:flex-initial text-sm font-medium"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <AssignRider />
              )}
            </div>
          </div>
        </div>
      </AppLayout>
    </>
  );
};

export default NotificationWrapper;

const TimelineItem = ({
  title,
  date,
  active = false,
}: {
  title: string;
  date?: string;
  active?: boolean;
}) => (
  <div className="flex items-start">
    <div
      className={`flex-shrink-0 w-3 h-3 mt-1.5 rounded-full ${
        active ? "bg-green-500" : "bg-gray-300"
      }`}
    ></div>
    <div className="ml-3">
      <p
        className={`text-sm font-medium ${
          active ? "text-gray-900" : "text-gray-500"
        }`}
      >
        {title}
      </p>
      {date && new Date(date).getFullYear() !== 1 ? (
        <p className="text-xs text-gray-500">
          {new Date(date).toLocaleString()}
        </p>
      ) : (
        <p className="text-xs text-gray-400">Pending</p>
      )}
    </div>
  </div>
);

const UserCard = ({
  id,
  type,
  title,
  icon,
}: {
  id: any;
  type: "user" | "agent";
  title: string;
  icon: string;
}) => (
  <div className="border border-gray-200 rounded-lg p-3 flex items-center">
    <span className="text-2xl mr-3">{icon}</span>
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      <Link
        to={`/pr-admin/${type}s/${type}-details/${
          typeof id === "string" ? id : id[0]?._id
        }`}
        className="font-medium hover:text-yellow-600 transition flex items-center gap-1"
      >
        View Details
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </Link>
    </div>
  </div>
);

const AttachmentPreview = ({ url, title }: { url: string; title: string }) => (
  <div className="border border-gray-200 rounded-lg p-3 w-full max-w-xs">
    <p className="text-sm font-medium mb-2">{title}</p>
    <div className="aspect-video bg-gray-100 rounded overflow-hidden">
      {/* Simple image preview - extend for PDFs etc */}
      <img src={url} alt={title} className="w-full h-full object-contain" />
    </div>
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block mt-2 text-sm text-yellow-600 hover:underline"
    >
      View Full Size
    </a>
  </div>
);
